import { generatePath } from 'react-router';
import { call, take, takeEvery, select, put, race } from 'redux-saga/effects';

import { PATH } from 'constants/RoutesConstants';
import { matchLocation } from 'modules/app/sagas/utils';
import { simpleRequest } from 'modules/app/utils';
import * as types from 'modules/auth/actionTypes';
import { selectFirstAvailableDealer } from 'modules/dealers/dealerSagas';
import { navigate } from 'modules/router/navigate';
import { getLocation } from 'modules/router/selectors';
import { createNotification } from 'modules/toast-notifications/actions';
import { CURRENT_USER_DATA_FAILURE, CURRENT_USER_DATA_SUCCESS } from 'modules/users/actionTypes';
import { getCurrentUserDealerIds } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId } from 'selectors/dealerSelectors';

import { loadSession } from '.';
import { stopImpersonateUser } from '../actions';

const disabledNotificationSettings = { success: () => undefined };

function* checkDealerAccess() {
  const dealerId: string = yield select(getSelectedDealerId);
  const dealerIds: string[] = yield select(getCurrentUserDealerIds);

  if (!dealerIds?.includes(dealerId)) {
    const location: ReturnType<typeof getLocation> = yield select(getLocation);
    yield call(selectFirstAvailableDealer, location.pathname);

    const isDealerConfig: boolean = yield call(matchLocation, [PATH.DEALER_CONFIG_PAGE]);
    if (isDealerConfig) {
      const to = generatePath(PATH.DEALER_CONFIG_PAGE.path, { dealerId, tabKey: 'general' });
      yield call(navigate, to, { replace: true });
    }
  }

  const isProfile: boolean = yield call(matchLocation, [PATH.PROFILE]);
  if (isProfile) {
    yield call(navigate, generatePath(PATH.PROFILE.path), { replace: true });
  }
}

function* failImpersonateFallback() {
  yield put(stopImpersonateUser());
  yield put(
    createNotification({
      type: 'error',
      message: {
        id: 'page.login.behalf.error',
        defaultMessage: 'impossible to impersonate the selected user, you will go back to your user',
      },
    })
  );
}

function* afterImpersonateActions() {
  yield call(loadSession, undefined, { forceRefresh: true });

  const { session, currentUser } = yield race({
    session: take([types.LOAD_SESSION_SUCCESS, types.LOAD_SESSION_FAILURE]),
    currentUser: take([CURRENT_USER_DATA_SUCCESS, CURRENT_USER_DATA_FAILURE]),
  });

  const hasError = session?.error || currentUser?.error;
  const callbackFn = hasError ? failImpersonateFallback : checkDealerAccess;
  yield call(callbackFn);
}

export default function* impersonateSaga() {
  yield takeEvery(types.IMPERSONATE_USER_ID, simpleRequest, { notifications: disabledNotificationSettings });
  yield takeEvery(types.STOP_IMPERSONATE_USER_ID, simpleRequest, { notifications: disabledNotificationSettings });
  yield takeEvery([types.IMPERSONATE_USER_ID_SUCCESS, types.STOP_IMPERSONATE_USER_ID_SUCCESS], afterImpersonateActions);
}
